import React, { useEffect, useRef, useState } from "react";
import { SliderRightArrowIcon } from "components/ui-kit/Icons/svg/index";
import useSlideGesture from "./useSlideGesture";

const ARROW_WIDTH_WITHOUT_LEFT_PADDING = 36;

const NavScrollWrapper = ({ children, orientation = "horizontal", classNames='' }) => {
  const navRef = useRef(null);
  const [showArrow, setShowArrow] = useState(false);
  const { isDragging, onMouseUp, onMouseDown, onMouseLeave, onMouseMove } =
    useSlideGesture({ containerRef: navRef });

  useEffect(() => {
    const navElement = navRef.current;

    if (!navElement) {
      return;
    }

    const handleButtonVisibility = () => {
      const fullWidth = navElement.scrollWidth;
      const visibleWidth = navElement.offsetWidth;
      const scrollPosition = navElement.scrollLeft;
      const isArrowVisible =
        Math.ceil(visibleWidth + scrollPosition) < fullWidth - 1;
      setShowArrow(isArrowVisible);
    };

    const navResize = new ResizeObserver(handleButtonVisibility);
    navElement.addEventListener("scroll", handleButtonVisibility);
    navResize.observe(navElement);

    return () => {
      navResize.disconnect();
      navElement.removeEventListener("scroll", handleButtonVisibility);
    };
  }, [navRef.current, navRef.current?.children?.length]);

  const handleOnClick = () => {
    const visibleWidth = navRef.current.offsetWidth;
    for (const child of navRef.current.children) {
      if (
        navRef.current.scrollLeft + visibleWidth >
        child.offsetLeft + child.offsetWidth
      ) {
        continue;
      }
      navRef.current.scrollLeft = child.offsetLeft;
      break;
    }
  };

  const handleOnItemSelect = (event) => {
    const isElementVisibleInRightPart =
      navRef.current.scrollLeft +
        navRef.current.offsetWidth -
        ARROW_WIDTH_WITHOUT_LEFT_PADDING <
      event.currentTarget.offsetLeft + event.currentTarget.offsetWidth;
    const isElementVisibleInLeftPart =
      navRef.current.scrollLeft > event.currentTarget.offsetLeft &&
      navRef.current.scrollLeft <
        event.currentTarget.offsetLeft + event.currentTarget.offsetWidth;

    if (isElementVisibleInRightPart || isElementVisibleInLeftPart) {
      navRef.current.scrollLeft = event.currentTarget.offsetLeft;
    }
  };

  const renderChildrenWithProps = () => {
    return React.Children.map(
      children,
      (child) =>
        React.isValidElement(child) &&
        React.cloneElement(child, {
          onClick: (event) => {
            child.props.onClick?.(event);
            handleOnItemSelect(event);
          },
        }),
    );
  };

  useEffect(() => {
    if (!navRef.current) {
      return;
    }

    const selectedItem = Array.from(navRef.current.children).find(
      (child) =>
        child.classList.contains("active") || child.querySelector?.(".active"),
    );

    if (!selectedItem) {
      return;
    }
    const isActiveElementNotFullVisible =
      navRef.current.scrollLeft + navRef.current.offsetWidth - 36 >
      selectedItem.offsetLeft + selectedItem.offsetWidth;

    if (isActiveElementNotFullVisible) {
      return;
    }

    navRef.current.scrollLeft = selectedItem.offsetLeft;
  }, []);

  return (
    <div className="relative w-full">
      <ul
        className={`my-0 flex list-none flex-nowrap items-center overflow-x-auto overflow-y-hidden whitespace-nowrap p-0 pb-0 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden ${isDragging ? "scroll-auto" : "scroll-smooth"} ${orientation === "vertical" ? `flex-col` : ""} ${classNames || ""}`.trim()}
        id="pills-tab"
        role="tablist"
        aria-orientation={orientation}
        ref={navRef}
        onMouseUp={onMouseUp}
        onMouseDown={onMouseDown}
        onMouseMove={onMouseMove}
        onMouseLeave={onMouseLeave}
      >
        {renderChildrenWithProps()}
      </ul>
      {showArrow && (
        <div className="pointer-events-none absolute bottom-0 right-0 top-0 w-12 bg-[linear-gradient(270deg,#1e1e1e_0%,rgba(15,25,35,0)_100%)]">
          <button
            className="pointer-events-auto relative left-3 top-1/2 flex h-6 w-6 -translate-y-1/2 items-center justify-center rounded-[0.313rem] border border-solid border-[var(--gray-80)] bg-[var(--gray-80)] p-0"
            onClick={handleOnClick}
            type="button"
            aria-label="Scroll tabs"
          >
            <SliderRightArrowIcon className="h-2.5 w-[0.313rem]" aria-hidden />
          </button>
        </div>
      )}
    </div>
  );
};

export default NavScrollWrapper;
